import React from 'react';
import { Popover } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { getVarianceColor } from 'src/utils';
import VARIANCE_COLORS from 'src/enums/varianceColor';
import FormattedNumber from 'src/components/common/FormattedNumber';
import ProductSummarySegment from '../../types/ProductSummarySegment';
import styles from './MPITable.module.less';

export type ColumnId = keyof ProductSummarySegment;
export type ProductSummarySegmentColumn = ColumnProps<ProductSummarySegment> & { dataIndex?: ColumnId };

export const getPriceColor = (price: number) => {
  if (price > 1) return VARIANCE_COLORS.positive;
  if (price < -1) return VARIANCE_COLORS.negative;
  return VARIANCE_COLORS.similar;
};

export const renderMPI = (record: ProductSummarySegment, isPriceRegular: boolean) => {
  const value = isPriceRegular ? record.mpi : record.lowestMpi;
  const backgroundColor = getVarianceColor(value);
  return <div style={{ backgroundColor: backgroundColor, color: '#fff' }}>{value ? value : ''}</div>;
};

export const Circle = ({ color }: { color: string }) => {
  return (
    <span className={styles.circle} style={{ backgroundColor: color }}>
      &nbsp;
    </span>
  );
};

export const renderMinPrice = (record: ProductSummarySegment, isPriceRegular: boolean) => {
  const value = isPriceRegular ? record.minRegularPrice : record.minLowestPrice;
  return <FormattedNumber type="currency" currency={'USD'} value={value} />;
};

export const renderMaxPrice = (record: ProductSummarySegment, isPriceRegular: boolean) => {
  const value = isPriceRegular ? record.maxRegularPrice : record.maxLowestPrice;
  return <FormattedNumber type="currency" currency={'USD'} value={value} />;
};

const renderPricePosition = (
  record: ProductSummarySegment,
  isPriceRegular: boolean,
  startPosition: number,
  endPosition: number,
  averagePrice: number
) => {
  const value = isPriceRegular ? record.regularPrice : record.lowestPrice;
  const zScore = isPriceRegular ? record.zScore : record.lowestZScore;
  if (isNaN(value)) return '';

  const price = endPosition === startPosition ? 0 : (value - startPosition) / (endPosition - startPosition);
  const pricePosition = `${(price * 100).toFixed()}%`;
  const color: string = getPriceColor(zScore);

  return (
    <div>
      {price < 0.9 ? (
        <span className={styles.marketPrice} style={{ left: pricePosition }}>
          <Circle color={color} />
          <FormattedNumber type="currency" currency={'USD'} value={value} />
        </span>
      ) : (
        <span className={styles.marketPriceRight}>
          <FormattedNumber type="currency" currency={'USD'} value={value} />
          <Circle color={color} />
        </span>
      )}
      <Popover content={'Average Price (Sum of All Prices / Count of Prices)'} placement={'right'}>
        <span className={styles.line} style={{ left: `${averagePrice}%` }}>
          <span className={styles.lineLabel}>Average</span>
        </span>
      </Popover>
    </div>
  );
};

const MPIHeader = (isPriceRegular: boolean) => {
  const formula = isPriceRegular
    ? 'Competitor regular price / Source regular price.'
    : 'Competitor lowest price / Source lowest price';
  const title = (
    <p className={styles.popover}>
      {formula}
    </p>
  );
  return <Popover content={title}>MPI</Popover>;
};

const MinPriceHeader = (isPriceRegular: boolean) => {
  const priceType = isPriceRegular ? 'regular' : 'lowest';
  const text = `The lowest seen ${priceType} price in the past 15 days.`;
  const title = <p className={styles.popover}>{text}</p>;
  return <Popover content={title}>Min Price</Popover>;
};

const MaxPriceHeader = (isPriceRegular: boolean) => {
  const priceType = isPriceRegular ? 'regular' : 'lowest';
  const text = `The highest seen ${priceType} price in the past 15 days.`;
  const title = <p className={styles.popover}>{text}</p>;
  return <Popover content={title}>Max Price</Popover>;
};

export const getColumns = (
  startPosition: number,
  endPosition: number,
  averagePrice: number,
  isPriceRegular: boolean
): ProductSummarySegmentColumn[] => {
  const columns: ProductSummarySegmentColumn[] = [
    {
      title: '',
      dataIndex: 'ownerName',
    },
    {
      title: MPIHeader(isPriceRegular),
      align: 'center',
      className: 'mpiColumn',
      render: (record) => renderMPI(record, isPriceRegular),
    },
    {
      title: 'Price Position In Market',
      align: 'center',
      className: 'pricePositionColumn',
      render: (record) => renderPricePosition(record, isPriceRegular, startPosition, endPosition, averagePrice),
    },
    {
      align: 'center',
      title: MinPriceHeader(isPriceRegular),
      render: (record) => renderMinPrice(record, isPriceRegular),
    },
    {
      align: 'center',
      title: MaxPriceHeader(isPriceRegular),
      render: (record) => renderMaxPrice(record, isPriceRegular),
    },
  ];

  return columns;
};
